import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { MdError } from 'react-icons/md';
import { toast } from 'react-toastify';
import ReactIcon from '../components/ReactIcon';
import { DateUtils } from '../helpers/dateUtils';
import { GetItemsShop } from '../services/salesShopItems.service';
import { useShoppingCartStore } from '../store/shoppingCart/shoppingCartStore';

type PropsGetShopItemsWaves = {
    Date?: Date;
    ProductId?: number;
    ProductTypeId?: number;
    ProductWaveSide?: 'Left' | 'Right';
    isFirstHeat?: boolean;
};

const currentDate = new Date();

export const useGetShopItemsWaves = ({
    Date,
    ProductId,
    ProductWaveSide,
    ProductTypeId,
    isFirstHeat,
}: PropsGetShopItemsWaves) => {
    const { hour, minute } = DateUtils.currentHourMinutSecond();
    const shoppingCartStore = useShoppingCartStore();
    const { isPending, data } = useQuery({
        queryKey: [
            'shopWaves',
            Date,
            ProductId,
            ProductWaveSide,
            ProductTypeId,
            isFirstHeat,
        ],
        select: data => {
            if (Date && !DateUtils.areDatesEqual(Date, currentDate))
                return data;

            //Filtrando para não aparecer horários inferiores ao do dispositivo do usuário
            const currentDeviceTime = moment(`${hour}:${minute}`, 'HH:mm');
            const filteredItems = data.map(item => {
                const filteredSchedules = item.schedules.filter(schedule => {
                    const scheduleDate = moment(schedule.name, 'HH:mm');
                    return scheduleDate.isAfter(currentDeviceTime);
                });

                return {
                    ...item,
                    schedules: filteredSchedules,
                };
            });

            // Filtrando para não aparecer ondas que não tem mais horários
            return filteredItems.filter(item => item.schedules.length > 0);
        },
        queryFn: async () => {
            const dateFormatted = DateUtils.formatToInitalYear(
                Date || currentDate
            );
            const response = await GetItemsShop({
                Date: dateFormatted,
                ProductId,
                ProductTypeCategory: 'Waves',
                ProductWaveSide,
                ProductTypeId,
                isFirstHeat,
            });

            const hasItemsInCart =
                shoppingCartStore.cartValues &&
                shoppingCartStore.cartValues.length > 0;

            if (hasItemsInCart) {
                response.forEach(item => {
                    if (shoppingCartStore.isPaymentLinkCart) return;
                    shoppingCartStore.updateItemPrice(item.id.toString(), item.value, dateFormatted);
                });
            }

            return response;
        },
        placeholderData: previousData => previousData,
        throwOnError: () => {
            toast.error(() => 'Erro ao atualizar as ondas disponíveis', {
                toastId: 1,
                style: {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                position: 'top-right',
                autoClose: 5000,
                icon: (
                    <ReactIcon
                        Icon={MdError}
                        size={'1.5rem'}
                        fill="#e40505"
                        color="white"
                    />
                ),
            });
            return false;
        },
    });

    return {
        isPending,
        data,
    };
};
